import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import PageTitle from "../../layouts/PageTitle";
import config from "../../../../config.json";
import { getMenuLanguages } from "../../../constants/menuLanguages";
import { choiceTitle, itemTitle } from "../Cms/MenuTree/menuTreeUtils";
import { formatDualMoney } from "../../../utils/currency";
import "../../../i18n";
import "./AddCategory.css";

const pickLocalized = (details, lang, field, fallback = "") => {
  const list = Array.isArray(details) ? details : [];
  const match = list.find((d) => d?.language === lang && String(d?.[field] || "").trim());
  if (match?.[field]) return match[field];
  const en = list.find((d) => d?.language === "en" && String(d?.[field] || "").trim());
  if (en?.[field]) return en[field];
  const first = list.find((d) => String(d?.[field] || "").trim());
  return first?.[field] || fallback;
};

const ChoiceDetails = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const languages = useMemo(() => getMenuLanguages(t), [t]);

  const [choice, setChoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openLangs, setOpenLangs] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const headers = { Authorization: `Bearer ${localStorage.getItem("tokenLogin")}` };
        const response = await axios.get(`${config.dev}/v2/choices/retrieve/${id}`, { headers });
        if (cancelled) return;
        const data = response?.data || {};
        const first = Array.isArray(data.items) ? data.items[0] : null;
        if (first && typeof first !== "object") {
          const itemsRes = await axios.get(`${config.dev}/v2/choices/getAllItems/${id}`, { headers });
          data.items = itemsRes.data?.items || [];
        }
        setChoice(data);
        setOpenLangs({});
      } catch (err) {
        console.error("Error fetching choice:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const isLangOpen = (code) => Boolean(openLangs[code]);
  const toggleLang = (code) => setOpenLangs((prev) => ({ ...prev, [code]: !prev[code] }));
  const usedLangs = languages.filter((lang) =>
    (choice?.choiceDetails || []).some(
      (d) => d.language === lang.code && String(d.choiceTitle || "").trim()
    )
  );
  const langsToShow = usedLangs.length ? usedLangs : languages.filter((lang) => lang.code === "en");

  const fallbackTitle = choiceTitle(choice, t("choiceDetails"));
  const items = (choice?.items || []).filter((item) => item && typeof item === "object" && item.isArchived !== true);

  if (loading) {
    return (
      <>
        <PageTitle activeMenu={t("choiceDetails")} motherMenu={t("kitchen")} />
        <div className="catform">
          <p className="catform__empty">{t("loading")}…</p>
        </div>
      </>
    );
  }

  if (!choice?._id) {
    return (
      <>
        <PageTitle activeMenu={t("choiceDetails")} motherMenu={t("kitchen")} />
        <div className="catform">
          <p className="catform__empty">{t("noTitle")}</p>
          <div className="catform__actions">
            <button
              type="button"
              className="btn catform__btn catform__btn--cancel"
              onClick={() => navigate("/choices")}
            >
              {t("choicesList")}
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageTitle activeMenu={t("choiceDetails")} motherMenu={t("kitchen")} />

      <div className="catform catform--sheet">
        <div className="catform__top catform__identity">
          <div className="catform__cover is-empty" aria-hidden>
            <i className="fas fa-list" />
          </div>
          <div className="catform__identity-copy">
            <p className="catform__kicker">{t("choiceSheetSubtitle")}</p>
            <h1 className="catform__title">{fallbackTitle}</h1>
            <div className="catform__facts">
              <span className="catform__fact">
                {t("minSelection")}: {choice.minSelected ?? 0}
              </span>
              <span className="catform__fact">
                {t("maxSelection")}: {choice.maxSelected ?? 0}
              </span>
              <span className="catform__fact">
                {items.length} {t("choiceFormItems").toLowerCase()}
              </span>
              {choice.obligatory && <span className="catform__fact">{t("hotRequired")}</span>}
              {choice.isFree && <span className="catform__fact">{t("freeChoice")}</span>}
            </div>
          </div>
          <div className="catform__top-actions">
            <button type="button" className="btn catform__link" onClick={() => navigate("/choices")}>
              <i className="fas fa-arrow-left" aria-hidden /> {t("choicesList")}
            </button>
            <Link to={`/edit-choice/${id}`} className="btn catform__btn catform__btn--edit">
              <i className="fas fa-pen" aria-hidden />
              {t("edit")}
            </Link>
          </div>
        </div>

        <section className="catform__section is-open">
          <h2 className="catform__heading">{t("catSheetLanguages")}</h2>
          <p className="catform__help">{t("sheetLangHint")}</p>
          <div className="catform__pills">
            {langsToShow.map((lang) => (
              <button
                key={lang.code}
                type="button"
                className={`catform__pill is-filled ${isLangOpen(lang.code) ? "is-open" : ""}`}
                aria-pressed={isLangOpen(lang.code)}
                onClick={() => toggleLang(lang.code)}
              >
                <img src={lang.flag} alt="" />
                {lang.label}
                <i className="fas fa-check" aria-hidden />
              </button>
            ))}
          </div>
          <div className="catform__lang-fields">
            {langsToShow
              .filter((lang) => isLangOpen(lang.code))
              .map((lang) => (
                <div
                  className="catform__lang-panel"
                  key={lang.code}
                  dir={lang.code === "ar" ? "rtl" : undefined}
                >
                  <div className="catform__field">
                    <span className="catform__label">
                      <img src={lang.flag} alt="" />
                      {lang.label} — {t("title")}
                    </span>
                    <p className="catform__readonly">
                      {pickLocalized(choice.choiceDetails, lang.code, "choiceTitle", t("noTitle"))}
                    </p>
                  </div>
                </div>
              ))}
          </div>
        </section>

        <section className="catform__section is-open">
          <h2 className="catform__heading">
            {t("choiceFormItems")}
            {items.length > 0 && <span className="catform__fold-count">{items.length}</span>}
          </h2>
          <div className="prod-choices__list catform__sheet-choices">
            {items.length === 0 ? (
              <div className="prod-choices__empty">
                <i className="fas fa-utensils" aria-hidden />
                <p>{t("choiceSheetNoItems")}</p>
              </div>
            ) : (
              items.map((item) => (
                <article key={String(item._id)} className="prod-choice is-checked">
                  <div className="prod-choice__head">
                    <span className="prod-choice__name">{itemTitle(item)}</span>
                    <span className="prod-choice__count">{formatDualMoney(item.price)}</span>
                  </div>
                </article>
              ))
            )}
          </div>
        </section>

        <div className="catform__actions">
          <button
            type="button"
            className="btn catform__btn catform__btn--cancel"
            onClick={() => navigate("/choices")}
          >
            {t("choicesList")}
          </button>
          <Link to={`/edit-choice/${id}`} className="btn catform__btn catform__btn--edit">
            <i className="fas fa-pen" aria-hidden />
            {t("edit")}
          </Link>
        </div>
      </div>
    </>
  );
};

export default ChoiceDetails;
